// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Clock, Construction, Loader2, SendHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";

interface SubmitForApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (message?: string) => void;
  isPending: boolean;
  actionLabel: string;
  summary: ReactNode;
  scheduledFor?: string;
}

export function SubmitForApprovalDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  actionLabel,
  summary,
  scheduledFor,
}: SubmitForApprovalDialogProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const [message, setMessage] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col">
        <div className="p-6 flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t("approvals.submitDialog.titleWithAction", { action: actionLabel })}</DialogTitle>
            <DialogDescription>{t("approvals.submitDialog.description")}</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs">
            <Construction className="h-3.5 w-3.5 shrink-0" />
            {t("common.workInProgress")}
          </div>

          <div className="rounded-md border p-3">{summary}</div>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("approvals.submitDialog.messagePlaceholder")}
            rows={2}
          />

          {scheduledFor && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {t("approvals.scheduledForFooter", {
                time: formatDateTime(scheduledFor, dateFormat),
              })}
            </div>
          )}
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={() => onSubmit(message || undefined)} disabled={isPending}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
            {t("buttons.submitForApproval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
